import reactUpdate from 'immutability-helper';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {updateStoichiometryCalculationSettingsActionFunction} from 'notebook/integrations/chemistry/StoichiometryCalculationSettingsModal';
import {
  downloadStoichiometryCSVActionFunction,
  exportStoichiometryAuditLogsActionFunction,
} from 'notebook/integrations/chemistry/stoichiometryGroupActionFunctions';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import GenericNoteLinkedGroupAdapter from 'notebook/notes/groups/GenericNoteLinkedGroupAdapter';
import {
  CreateNoteLinkedGroupProps,
  GroupActionFunctions,
  NoteLinkedGroup,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {STRUCTURED_TABLE_PLUGIN} from 'notebook/structured-tables/StructuredTablePlugin';
import {
  EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  StructuredTableInsertionContextValue,
} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {isStructuredTable} from 'notebook/TypePredicates';
import {NotePart, NotePartWithItem} from 'notebook/util/NoteUtilConstants';
import {Schema} from 'schemas/SchemaConstants';

import {StoichiometryStructuredTable} from './StoichiometryStructuredTableAdapter';

export interface StoichiometryGroupMetadata {
  reactionId: string | null;
}
export type StoichiometryGroup = NoteLinkedGroup<StoichiometryGroupMetadata>;

export interface StoichiometryCreateGroupExtras {
  structuredTableInsertionContext?: StructuredTableInsertionContextValue;
}

export class StoichiometryNoteLinkedGroupAdapter extends GenericNoteLinkedGroupAdapter<
  StoichiometryGroupMetadata,
  StoichiometryCreateGroupExtras
> {
  getDisplayName(_group: StoichiometryGroup): string {
    return 'Stoichiometry';
  }

  override getActionFunctions(): GroupActionFunctions<
    StoichiometryGroupMetadata,
    StoichiometryCreateGroupExtras
  > {
    const inherited = super.getActionFunctions();
    return {
      ...inherited,
      secondary: [updateStoichiometryCalculationSettingsActionFunction, ...(inherited.secondary ?? [])],
      overflow: [
        ...(inherited.overflow ?? []),
        exportStoichiometryAuditLogsActionFunction,
        downloadStoichiometryCSVActionFunction,
      ],
    };
  }

  createNoteParts(
    createProps: CreateNoteLinkedGroupProps<StoichiometryGroupMetadata, StoichiometryCreateGroupExtras>
  ): Array<NotePart> {
    assert(
      createProps.kind === this.kind,
      `Stoichiometry adapter only supports ${this.kind}, got ${createProps.kind}`
    );
    const {name, entryId, selectorsById, isConfig, extras} = createProps;
    const stoichiometryTable = STRUCTURED_TABLE_PLUGIN.get(StructuredTableKind.STOICHIOMETRY).createTable({
      name,
      schemas: [],
      entryId,
      metadata: {},
      selectorsById,
      isConfig,
      structuredTableInsertionContext:
        extras.structuredTableInsertionContext ?? EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
    });
    return [
      // @ts-expect-error(2322): This is passing in `shouldGenerateCid: false` but we are expecting it to have a cid
      reactUpdate(makeDefaultItem(ModelTypeConstants.NOTE_LINKED_OBJECT, null, {shouldGenerateCid: false}), {
        // @ts-expect-error(2322): The NotePart StructuredTable mapping uses unknown instead of a type parameter
        item: {$set: stoichiometryTable},
      }),
    ];
  }

  override supportsCopyPaste(): boolean {
    return true;
  }

  override requiresTableEvaluationOnInsert(): boolean {
    return true;
  }

  getGroupSchemas(_group: StoichiometryGroup): Array<Schema> {
    return [];
  }

  getReactionStructuredTableNotePart(
    group: StoichiometryGroup
  ): NotePartWithItem<StoichiometryStructuredTable> {
    const reactionTableNotePart = group.containedNoteParts.find(
      (part) =>
        part.item && isStructuredTable(part.item) && part.item.kind === StructuredTableKind.STOICHIOMETRY
    );
    assert(
      reactionTableNotePart != null,
      'Reaction Structured Table must exist inside Reaction Note Linked Group'
    );
    return reactionTableNotePart as NotePartWithItem<StoichiometryStructuredTable>;
  }

  getReactionStructuredTable(group: StoichiometryGroup): StoichiometryStructuredTable {
    return this.getReactionStructuredTableNotePart(group).item;
  }
}
